import React from 'react';
import { Box, Button, Menu, MenuItem, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

import { r, color, radius, tabularNums } from '../../../theme/tokens';
import CreateStackButton from '../../CreateStackButton';
import StackCard from '../../common/StackCard';
import {
  SECTION_HEADING_MY_STACKS,
  MY_STACKS_SUBHEAD,
  MY_STACKS_SUBHEAD_LAPSED,
  interactionFailureLine,
} from '../copy';

/**
 * "My stacks" — §1's only section-level heading, and the page's main section.
 *
 * It sits on paper with no band and no divider rule above it (§1). That is
 * what keeps the Recommended band reading as the subordinate, contained zone:
 * the band has a surface and a quiet eyebrow, this has a real heading and no
 * container at all.
 *
 * Contains only stacks the user built. A pinned recommended stack stays in the
 * Recommended band and does not appear here — §5, ruled 2026-08-20, and its
 * own helper copy says so ("Pin to keep it in this row"). This supersedes
 * frame 1.2 of the older handoff, which converted a pinned card into an owned
 * one and would have shown it in both places.
 *
 * Sort applies here and only here. §5: it "never reorders the Recommended
 * band", which is structurally true because the band is rendered by a sibling
 * that never sees `sortBy`.
 */

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recent' },
  { value: 'name', label: 'A–Z' },
];

const EMPTY_HEADING = "Let's build your stack";
// "…or analyze your current supplements" came out on 2026-09-12 with the manual
// creation entry point it described; the line now says what New stack does.
const EMPTY_BODY =
  'Get a personalized, evidence-based supplement stack, built around your health goals';

export default function MyStacksSection({
  stacks,
  supplementMap,
  shareUrls,
  showShareField,
  sortBy,
  onSortChange,
  interactionStatuses,
  checkingInteractions,
  userMedicationCount,
  hasInteractionAccess,
  onView,
  onEdit,
  onDelete,
  onTogglePublic,
  onShare,
  onShareFieldClose,
  onCopyToClipboard,
  formatDate,
  // Swaps only the subhead. Everything else in this section is the member's
  // own work and is untouched by a lapsed membership — which is exactly what
  // the swapped line says.
  membershipPaused = false,
}) {
  // §8: accordion below 640px, fully expanded from 1024px. The breakpoint that
  // decides is the viewport's, so it is read here and passed down rather than
  // guessed inside the card.
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:39.9375rem)');
  // `md` — the width at which the grid below goes from 1-up to 3-up. The New
  // stack card only earns its place once there is grid space beside the stacks;
  // see where it renders.
  //
  // Taken from the theme rather than written out, unlike `isMobile` above: this
  // one has to agree *exactly* with the `md` key the grid's own
  // `gridTemplateColumns` uses, and a hand-written `(min-width:56.25rem)` only
  // agrees with MUI's `900px` while the root font-size is 16px. The accordion
  // breakpoint above answers to §8 alone and has nothing to stay in step with.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);

  const activeSort = SORT_OPTIONS.find((option) => option.value === sortBy) || SORT_OPTIONS[0];

  return (
    <Box component="section" data-testid="my-stacks" sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Box
              component="h2"
              sx={{
                margin: 0,
                // §8 drops the heading to 17px on mobile; §1 sets 20px/800.
                fontSize: { xs: '1.0625rem', sm: '1.25rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                // Evergreen, not `ink`. The two tokens disagreed about who
                // owns headings — `ink` calls itself "body text, headings"
                // while §1 gives the greeting `evergreen` — and Tanu settled
                // it on 2026-08-26: the section heading matches the greeting,
                // so the page speaks in one voice. Renders 4a/4c/4e draw it
                // this way too.
                color: color.evergreen,
              }}
            >
              {SECTION_HEADING_MY_STACKS}
            </Box>
            <Box
              component="span"
              data-testid="my-stacks-count"
              sx={{
                paddingInline: '0.5rem',
                minWidth: '1.5rem',
                textAlign: 'center',
                borderRadius: radius.pill,
                backgroundColor: color.surface,
                border: `${r(1)} solid ${color.line}`,
                color: color.inkSoft,
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: '1.375rem',
                ...tabularNums,
              }}
            >
              {stacks.length}
            </Box>
          </Box>
          <Box
            sx={{
              marginTop: '0.25rem',
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.5,
            }}
          >
            {/* A lapsed member is told their own stacks are unaffected by the
                membership, and deliberately not that they are "still
                editable": interaction checking is gated, so those stacks are
                no longer screened against their medications. Overstating that
                is the one thing this line must not do. */}
            {membershipPaused ? MY_STACKS_SUBHEAD_LAPSED : MY_STACKS_SUBHEAD}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <Button
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(event) => setSortAnchorEl(event.currentTarget)}
            aria-label={`Sort: ${activeSort.label}`}
            sx={{
              // §8 names the sort control as a 44×44 hit target.
              minHeight: '2.75rem',
              paddingInline: '0.75rem',
              borderRadius: radius.md,
              border: `${r(1)} solid ${color.line}`,
              backgroundColor: color.paper,
              color: color.ink,
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: color.surface },
              '&:focus-visible': {
                outline: `${r(2)} solid ${color.accent}`,
                outlineOffset: r(2),
              },
            }}
          >
            {activeSort.label}
          </Button>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={() => setSortAnchorEl(null)}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={sortBy === option.value}
                onClick={() => {
                  onSortChange(option.value);
                  setSortAnchorEl(null);
                }}
                sx={{ minHeight: '2.75rem', fontSize: '0.875rem' }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
          <CreateStackButton />
        </Box>
      </Box>

      {stacks.length === 0 ? (
        <Box
          sx={{
            marginTop: '2rem',
            padding: '2rem 1.25rem',
            borderRadius: radius.lg,
            border: `${r(1)} dashed ${color.line}`,
            textAlign: 'center',
          }}
        >
          <Box
            component="p"
            sx={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: color.ink }}
          >
            {EMPTY_HEADING}
          </Box>
          <Box
            component="p"
            sx={{
              // No `maxWidth` — a 30rem clamp broke this onto two lines on
              // desktop where it fits on one (Tanu, 2026-08-27). It still
              // wraps on a phone, which is unavoidable and fine.
              margin: '0.5rem auto 1.25rem',
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.6,
            }}
          >
            {EMPTY_BODY}
          </Box>
          <CreateStackButton />
        </Box>
      ) : (
        <Box
          data-testid="my-stacks-grid"
          sx={{
            marginTop: '1.25rem',
            display: 'grid',
            // 1-up mobile, 3-up desktop — §8.
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: '1rem',
            alignItems: 'stretch',
          }}
        >
          {stacks.map((stack) => (
            <StackCard
              key={stack.id}
              stack={stack}
              collapsible={isMobile}
              supplementMap={supplementMap}
              // §6: a Public stack always has a live link. Prefer a URL the
              // parent already minted (e.g. on toggle), otherwise derive it so
              // a reload of an already-public stack still has something to
              // copy — the × only hides the row for the session.
              shareUrl={
                shareUrls[stack.id] ||
                (stack.isPublic && typeof window !== 'undefined'
                  ? `${window.location.origin}/view-stack/${stack.id}`
                  : '')
              }
              // §6 + handleShareFieldClose's own comment: the × hides the row
              // from view only; "the row returns on next load". An absent
              // entry must therefore mean "show" for a public stack, not
              // "hide" — otherwise a reload after × (or any load of a stack
              // that was already public) leaves the row missing.
              showShareField={
                Boolean(stack.isPublic) && showShareField[stack.id] !== false
              }
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onTogglePublic={onTogglePublic}
              onShare={onShare}
              onShareFieldClose={onShareFieldClose}
              onCopyToClipboard={onCopyToClipboard}
              formatDate={() => formatDate(stack.jsUpdatedAt)}
              interactionStatus={
                hasInteractionAccess
                  ? checkingInteractions[stack.id]
                    ? 'PROCESSING'
                    : interactionStatuses[stack.id]?.status || null
                  : null
              }
              hasInteractions={
                hasInteractionAccess
                  ? Boolean(interactionStatuses[stack.id]?.hasInteractions)
                  : false
              }
              // A check that could not run (STACK-542). `interactionFailureLine`
              // answers null unless a failure kind was recorded, so a clean or
              // still-running check leaves the slot empty exactly as before.
              interactionFailureLine={
                hasInteractionAccess
                  ? interactionFailureLine(interactionStatuses[stack.id]?.failureKind)
                  : null
              }
              supplementInteractions={
                hasInteractionAccess
                  ? interactionStatuses[stack.id]?.supplementInteractions || {}
                  : {}
              }
              userMedicationCount={userMedicationCount}
              // No disclaimer on a My stacks card, and none under the grid
              // either (Tanu, 2026-09-14 — STACK-541). The card is a summary
              // that opens into View and Edit, and both of those carry the
              // line through `SupplementsTable`; the rule is now one
              // disclaimer on the surface where the reader actually reads the
              // stack, and this is not that surface. STACK-517 had put one
              // under the grid instead of one per cell, which fixed the
              // repetition but still said it on a screen that defers.
              hideDisclaimer
            />
          ))}
          {/* Always the last cell (Tanu, 2026-08-26), so the entry point never
              leaves the grid as the grid fills. Render 4b draws it beside a
              single stack; with three it wraps onto a second row, which the
              renders never had to show. The empty state keeps its own panel
              below — a fuller invitation than a bare dashed cell.

              Desktop only (Tanu, 2026-08-29). The grid is 1-up below `md`, so
              the dashed cell became a full-width block under the last stack —
              a second New stack control stacked beneath the one in the header,
              with none of the "empty space in the grid" that motivates it.
              Dropped rather than hidden with CSS: mobile keeps an entry point
              in the header `<CreateStackButton />` above, which carries no
              responsive hiding of its own, and the empty state's own button is
              in a different branch entirely. Read here through the same
              `useMediaQuery` the accordion breakpoint uses — §8's rule that the
              viewport's breakpoint is read by the component that owns the
              layout — which also keeps the dashed cell a direct grid child
              rather than putting a wrapper between it and the grid. */}
          {isDesktop && <CreateStackButton asCard />}
        </Box>
      )}
    </Box>
  );
}
